import { Link, useParams } from "react-router";
import MainLayout from "@/components/layouts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { InvoiceBillTo, InvoiceLineItemsTable, InvoicePdfButton } from "@/components/shared/invoices/invoice-sections";
import { useInvoice } from "@/app/vendors/invoices/invoices.hooks";
import { getInvoiceSubject } from "@/lib/invoices/invoice-subject";
import { invoiceBreadcrumb } from "@/lib/invoices/invoice-breadcrumb";
import type { InvoiceRecord } from "@/repositories/invoice-records";
import { CustomerInvoiceStatusBadge } from "./customer-invoice-status-badge";
import { formatInvoiceDate } from "./format-invoice-date";

function InvoiceFrom({ invoice }: { invoice: InvoiceRecord }) {
  const vendor = invoice.vendor_data;
  return (
    <section aria-label="From">
      <p className="text-sm text-muted-foreground uppercase">From:</p>
      <p className="font-medium mt-4">{vendor.name}</p>
      {vendor.location && <p className="text-sm whitespace-pre-line">{vendor.location}</p>}
      {vendor.phone && <p className="text-sm text-muted-foreground">{vendor.phone}</p>}
    </section>
  );
}

function InvoiceBilledFor({ invoice }: { invoice: InvoiceRecord }) {
  const subject = getInvoiceSubject(invoice);
  return (
    <section aria-label="Billed for">
      <p className="text-sm text-muted-foreground uppercase">Billed For:</p>
      <p className="font-medium mt-4">{subject.propertyName ?? "N/A"}</p>
      {subject.unitNumber && <p className="text-sm text-muted-foreground">Unit: {subject.unitNumber}</p>}
      {subject.presentation.detailServiceTitle && <p className="text-sm mt-2">{subject.presentation.detailServiceTitle}</p>}
      {subject.presentation.detailServiceDescription && <p className="text-sm text-muted-foreground">{subject.presentation.detailServiceDescription}</p>}
      {subject.kind === "appointment" && subject.href && (
        <p className="text-sm mt-2">
          <Link to={subject.href} className="text-primary hover:underline">
            View appointment
          </Link>
        </p>
      )}
    </section>
  );
}

export default function CustomerInvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { invoice, loading, error } = useInvoice(invoiceId ?? null);

  const breadcrumb = invoiceBreadcrumb({ invoice, loading, error });

  if (loading && !invoice) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <Card className="max-w-4xl mx-auto space-y-6 p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-64 w-full" />
        </Card>
      </MainLayout>
    );
  }

  if (!invoice) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <Card className="max-w-4xl mx-auto p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-destructive mb-2">{error ?? "Invoice not found"}</p>
              <Link to="/invoices" className="text-primary hover:underline">
                Back to Invoices
              </Link>
            </div>
          </div>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <Card className="max-w-4xl mx-auto space-y-6 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Invoice #{invoice.invoice_number}</h2>
          <CustomerInvoiceStatusBadge invoice={invoice} />
        </div>

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <InvoiceFrom invoice={invoice} />
          <InvoiceBillTo invoice={invoice} />
          <InvoiceBilledFor invoice={invoice} />
        </div>

        <div className="text-sm text-muted-foreground">
          <p>Issue Date: {formatInvoiceDate(invoice.issue_date)}</p>
          <p>Due Date: {formatInvoiceDate(invoice.due_date)}</p>
          {invoice.paid_at && <p>Paid At: {formatInvoiceDate(invoice.paid_at)}</p>}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent>
            <InvoiceLineItemsTable invoice={invoice} />
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <InvoicePdfButton invoice={invoice} label="Download PDF" />
        </div>
      </Card>
    </MainLayout>
  );
}
